import cls from "./shopHeader.module.scss";
import { IShop } from "interfaces";
import useShopWorkingSchedule from "hooks/useShopWorkingSchedule";
import useLiked from "hooks/useLiked";
import Bookmark3LineIcon from "remixicon-react/Bookmark3LineIcon";
import InformationLineIcon from "remixicon-react/InformationLineIcon";
import TimerFlashLineIcon from "remixicon-react/TimerFlashLineIcon";
import StarFillIcon from "remixicon-react/StarFillIcon";
import { useTranslation } from "react-i18next";
import Bookmark3FillIcon from "remixicon-react/Bookmark3FillIcon";
import useModal from "hooks/useModal";
import dynamic from "next/dynamic";
import { Skeleton, useMediaQuery } from "@mui/material";
import FallbackImage from "components/fallbackImage/fallbackImage";
import Message3LineIcon from "remixicon-react/Message3LineIcon";
import Loading from "components/loader/loading";
import Link from "next/link";
import { useRouter } from "next/router";
import FEATURES from "constants/features"; // ✅ flag ricette

const DrawerContainer = dynamic(() => import("containers/drawer/drawer"));
const MobileDrawer = dynamic(() => import("containers/drawer/mobileDrawer"));
const ShopInfo = dynamic(() => import("components/shopInfo/shopInfo"), {
  loading: () => <Loading />,
});
const Reviews = dynamic(() => import("components/review/review"), {
  loading: () => <Loading />,
});

type Props = {
  data?: IShop;
  loading?: boolean;
  handleOpenChat?: () => void;
  handleCloseChat?: () => void;
};

export default function ShopHeader({ data, loading, handleOpenChat }: Props) {
  const { t } = useTranslation();
  const { query } = useRouter();
  const [open, handleOpen, handleClose] = useModal();
  const [openReviews, handleOpenReviews, handleCloseReviews] = useModal();
  const isDesktop = useMediaQuery("(min-width: 1140px)");
  const isMobile = useMediaQuery("(max-width: 700px)");

  const { workingSchedule, isShopClosed } = useShopWorkingSchedule(data);
  const { isLiked, handleLikeOrDislike } = useLiked({
    type: "shop",
    itemId: data?.id || 0,
  });

  if (loading) {
    return (
      <div className={cls.shimmerContainer}>
        <Skeleton className={cls.shimmer} variant="rectangular" />
      </div>
    );
  }

  const renderWorkingSchedule = () => (
    <div className={cls.workingSchedule}>
      <TimerFlashLineIcon size={22} />
      {!isShopClosed && !workingSchedule?.disabled
        ? `${t("by")} ${workingSchedule?.from} - ${workingSchedule?.to}`
        : t("closed")}
    </div>
  );

  return (
    <>
      <div className={cls.header}>
        <div className={cls.backgroundImageContainer}>
          {!isDesktop && (
            <div className={cls.tabletWorkingSchedule}>
              {renderWorkingSchedule()}
            </div>
          )}
          <FallbackImage
            fill
            priority
            src={data?.background_img}
            alt={data?.translation?.title || t("shop.background")}
            sizes="1200px"
          />
          <div className={cls.logoContainer}>
            <div className={cls.logoWrapper}>
              <FallbackImage
                src={data?.logo_img}
                alt={data?.translation?.title || t("shop.logo")}
                fill
                sizes="200px"
              />
            </div>
          </div>
        </div>
        <div className={cls.infoContainer}>
          <div className={cls.infoWrapper}>
            <span className={cls.title}>{data?.translation?.title}</span>
            <div className={cls.verticalLine} />
            <button
              type="button"
              className={cls.action}
              onClick={() => handleLikeOrDislike()}
            >
              {isLiked ? (
                <Bookmark3FillIcon size={22} />
              ) : (
                <Bookmark3LineIcon size={22} />
              )}
              <span className={cls.text}>{t(isLiked ? "saved" : "save")}</span>
            </button>
            <div className={cls.verticalLine} />
            <button type="button" className={cls.action} onClick={handleOpen}>
              <InformationLineIcon size={22} />
              <span className={cls.text}>{t("shop.info")}</span>
            </button>
            <div className={cls.verticalLine} />
            {isDesktop && (
              <>
                {renderWorkingSchedule()}
                <div className={cls.verticalLine} />
              </>
            )}
            <button
              type="button"
              className={cls.rating}
              onClick={handleOpenReviews}
            >
              <StarFillIcon size={24} />
              {data?.r_avg?.toFixed(1) || 0}
            </button>
            {isMobile && (
              <button
                type="button"
                className={cls.chat}
                onClick={() => handleOpenChat && handleOpenChat()}
              >
                <Message3LineIcon />
              </button>
            )}

            {/* ✅ Sezione link mobile (Ricette + Prodotti salvati) */}
            {!isDesktop && (
              <>
                <div className={cls.verticalLine} />

                {/* Link Ricette visibile solo se il modulo è attivo */}
                {FEATURES.recipes && (
                  <>
                    <Link
                      href={{ pathname: "/recipes", query: { shop_id: query?.id } }}
                      className={cls.link}
                    >
                      {t("recipes")}
                    </Link>
                    <div className={cls.verticalLine} />
                  </>
                )}

                <Link
                  href={{
                    pathname: "/liked/products",
                    query: { shop_id: query?.id },
                  }}
                  className={cls.link}
                >
                  {t("liked.products")}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      {isMobile ? (
        <MobileDrawer open={open} onClose={handleClose} title={t("shop.info")}>
          <ShopInfo data={data} />
        </MobileDrawer>
      ) : (
        <DrawerContainer
          open={open}
          onClose={handleClose}
          anchor="right"
          title={t("shop.info")}
          PaperProps={{ style: { minWidth: isMobile ? "100%" : "400px" } }}
        >
          <ShopInfo data={data} />
        </DrawerContainer>
      )}
      <DrawerContainer
        open={openReviews}
        onClose={handleCloseReviews}
        anchor="right"
        PaperProps={{
          style: { padding: 0, minWidth: isMobile ? "100%" : "400px" },
        }}
      >
        <Reviews type="shops" typeId={data?.id || 0} />
      </DrawerContainer>
    </>
  );
}
